import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  BookOpen,
  Download,
  ShieldAlert,
  Trash2,
  Accessibility,
  Plus,
  Pencil,
  Check,
  X,
  HelpCircle,
  MessageSquarePlus,
  KeyRound,
  Sparkles,
  Copy,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  UserRound,
  Lock,
  Briefcase,
  SlidersHorizontal,
  Gift,
  Database,
  Smartphone,
} from "lucide-react";
import {
  showAccessibilityMenu,
  isAccessibilityDisabled,
  setAccessibilityDisabled,
} from "@/components/AccessibilityMenu";
import { InviteCard } from "@/components/InviteCard";
import { TrashCard } from "@/components/TrashCard";
import { useBusiness } from "@/components/business-context";
import { usePrefs } from "@/components/prefs-context";
import { getSupabase } from "@/lib/supabase-lazy";
import { getAccount, exportData, exportForAi, deleteAccount } from "@/lib/account.functions";
import { submitFeedback, listMyFeedback, saveA11yDisabled } from "@/lib/prefs.functions";
import { BetaProgress } from "@/components/BetaProgress";
import {
  AUTO_LOCK_OPTIONS,
  AUTO_LOCK_DEFAULT,
  getAutoLockMinutes,
  setAutoLockMinutes,
} from "@/lib/auto-lock";

import { SECTION_HELP } from "@/lib/sections";
import {
  createBusiness,
  renameBusiness,
  deleteBusiness,
  reorderBusinesses,
  changePin,
} from "@/lib/momentum.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/components/ThemeSwitcher";
import { THEMES } from "@/lib/themes";

export const Route = createFileRoute("/_authenticated/app/settings")({
  head: () => ({
    meta: [
      { title: "Settings — The Founders App" },
      { property: "og:title", content: "Settings — The Founders App" },
    ],
  }),
  component: SettingsPage,
});

const OPEN_KEY = "founders.settings.section";

type SectionId = "account" | "security" | "businesses" | "app" | "invite" | "data" | "danger";

function Panel({
  id,
  title,
  summary,
  icon,
  open,
  onToggle,
  danger,
  children,
}: {
  id: SectionId;
  title: string;
  summary: string;
  icon: ReactNode;
  open: boolean;
  onToggle: (id: SectionId) => void;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`soft-panel mt-3 overflow-hidden ${danger ? "border-destructive/40" : ""}`}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        aria-controls={`section-${id}`}
        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-5 py-4 text-left"
      >
        <span
          aria-hidden="true"
          className={`shrink-0 ${danger ? "text-destructive" : "text-muted-foreground"}`}
        >
          {icon}
        </span>
        <span className="min-w-0">
          <span
            className={`block text-sm font-medium ${danger ? "text-destructive" : "text-foreground"}`}
          >
            {title}
          </span>
          <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{summary}</span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div id={`section-${id}`} className="border-t border-border px-5 pb-6 pt-5">
          {children}
        </div>
      )}
    </section>
  );
}

function SettingsPage() {
  const navigate = useNavigate();
  const account = useQuery({ queryKey: ["account"], queryFn: () => getAccount() });
  const runExport = useServerFn(exportData);
  const runDelete = useServerFn(deleteAccount);

  const [openSection, setOpenSection] = useState<SectionId | null>("account");
  useEffect(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      if (saved === "") setOpenSection(null);
      else if (saved) setOpenSection(saved as SectionId);
    } catch {
      /* ignore */
    }
  }, []);
  const toggleSection = (id: SectionId) => {
    setOpenSection((cur) => {
      const next = cur === id ? null : id;
      try {
        localStorage.setItem(OPEN_KEY, next ?? "");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const { theme, setTheme } = useTheme();
  const { hidden, setHidden, openHelp } = usePrefs();
  const [confirm, setConfirm] = useState("");
  const [dangerOpen, setDangerOpen] = useState(false);

  const [busy, setBusy] = useState(false);

  const runSaveA11y = useServerFn(saveA11yDisabled);

  const [a11yOff, setA11yOff] = useState(() => isAccessibilityDisabled());

  const runChangePin = useServerFn(changePin);
  const [curPin, setCurPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [autoLock, setAutoLock] = useState<number>(AUTO_LOCK_DEFAULT);

  useEffect(() => {
    setAutoLock(getAutoLockMinutes());
  }, []);

  const [feedbackCategory, setFeedbackCategory] = useState<"bug" | "idea">("idea");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const myFeedback = useQuery({ queryKey: ["my-feedback"], queryFn: () => listMyFeedback() });

  const sendFeedback = async () => {
    const message = feedbackText.trim();
    if (!message || feedbackBusy) return;
    setFeedbackBusy(true);
    try {
      await submitFeedback({
        data: { category: feedbackCategory, message, page: "settings" },
      });
      setFeedbackText("");
      toast.success("Thank you — it's logged.");
      void myFeedback.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send that");
    } finally {
      setFeedbackBusy(false);
    }
  };

  const { businesses, active, refresh } = useBusiness();
  const runCreate = useServerFn(createBusiness);
  const runRename = useServerFn(renameBusiness);
  const runDeleteBusiness = useServerFn(deleteBusiness);
  const runReorder = useServerFn(reorderBusinesses);
  const [order, setOrder] = useState<string[] | null>(null);
  const orderedBusinesses = order
    ? [...businesses].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    : businesses;

  const moveBusiness = async (id: string, direction: -1 | 1) => {
    const ids = orderedBusinesses.map((b) => b.id);
    const from = ids.indexOf(id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to]!, ids[from]!];
    setOrder(ids);
    try {
      await runReorder({ data: { ids } });
      refresh();
    } catch (err) {
      setOrder(null);
      toast.error(err instanceof Error ? err.message : "Could not reorder");
    }
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [newName, setNewName] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [businessBusy, setBusinessBusy] = useState(false);

  const armed = confirm.trim().toUpperCase() === "DELETE";

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toCsv = (payload: Record<string, unknown>) => {
    const esc = (v: unknown) => {
      if (v === null || v === undefined) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const blocks: string[] = [];
    for (const [section, rows] of Object.entries(payload)) {
      if (!Array.isArray(rows) || rows.length === 0) continue;
      const cols = Array.from(
        new Set(rows.flatMap((r) => Object.keys(r as Record<string, unknown>))),
      );
      blocks.push(
        `# ${section}\n${cols.join(",")}\n` +
          rows
            .map((r) => cols.map((c) => esc((r as Record<string, unknown>)[c])).join(","))
            .join("\n"),
      );
    }
    return blocks.join("\n\n");
  };

  const downloadExport = async (format: "json" | "csv" = "json") => {
    try {
      const payload = (await runExport(undefined as never)) as Record<string, unknown>;
      const stamp = new Date().toISOString().slice(0, 10);
      if (format === "csv") {
        downloadBlob(
          new Blob([toCsv(payload)], { type: "text/csv;charset=utf-8" }),
          `founders-export-${stamp}.csv`,
        );
      } else {
        downloadBlob(
          new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }),
          `founders-export-${stamp}.json`,
        );
      }
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    }
  };

  const runAiFn = useServerFn(exportForAi);
  const todayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const daysAgoKey = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [exportFormat, setExportFormat] = useState<"json" | "csv" | "markdown">("json");
  const [aiBusinessId, setAiBusinessId] = useState("");
  const [aiStart, setAiStart] = useState(daysAgoKey(30));
  const [aiEnd, setAiEnd] = useState(todayKey());
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => {
    if (!aiBusinessId && businesses.length > 0) {
      setAiBusinessId(active?.id ?? businesses[0]!.id);
    }
  }, [aiBusinessId, active, businesses]);

  const runAiExport = async (mode: "download" | "copy") => {
    if (aiBusy || !aiBusinessId) return;
    setAiBusy(true);
    try {
      const res = (await runAiFn({
        data: { businessId: aiBusinessId, start: aiStart, end: aiEnd },
      })) as { markdown: string; businessName: string };
      if (mode === "copy") {
        await navigator.clipboard.writeText(res.markdown);
        toast.success("Copied — paste it into your AI chat.");
      } else {
        const slug = res.businessName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "");
        downloadBlob(
          new Blob([res.markdown], { type: "text/markdown;charset=utf-8" }),
          `${slug || "business"}-log-${aiStart}-to-${aiEnd}.md`,
        );
        toast.success("Export downloaded");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setAiBusy(false);
    }
  };

  const wipe = async () => {
    if (!armed || busy) return;
    setBusy(true);
    try {
      await runDelete({ data: { confirm: "DELETE" } });
      sessionStorage.removeItem("momentum.unlocked");
      localStorage.removeItem("momentum.activeBusiness");
      await (await getSupabase()).auth.signOut();
      toast.success("Your account and everything in it has been deleted.");
      void navigate({ to: "/" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete the account");
      setBusy(false);
    }
  };

  const startEdit = (b: { id: string; name: string }) => {
    setEditingId(b.id);
    setEditName(b.name);
    setDeletingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const saveEdit = async () => {
    if (!editingId || !editName.trim() || businessBusy) return;
    setBusinessBusy(true);
    try {
      await runRename({ data: { id: editingId, name: editName.trim() } });
      toast.success("Business renamed");
      cancelEdit();
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not rename");
    } finally {
      setBusinessBusy(false);
    }
  };

  const confirmDelete = async (id: string) => {
    if (businessBusy) return;
    setBusinessBusy(true);
    try {
      await runDeleteBusiness({ data: { id } });
      toast.success("Business removed");
      setDeletingId(null);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not remove");
    } finally {
      setBusinessBusy(false);
    }
  };

  const addBusiness = async () => {
    const name = newName.trim();
    if (!name || businessBusy) return;
    setBusinessBusy(true);
    try {
      await runCreate({ data: { name } });
      toast.success(`${name} added`);
      setNewName("");
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not add business");
    } finally {
      setBusinessBusy(false);
    }
  };

  const activeTheme = THEMES.find((t) => t.id === theme) ?? THEMES[0]!;
  const hiddenCount = SECTION_HELP.filter((s) => s.hideable && hidden.includes(s.key)).length;

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3">
        <div className="min-w-0">
          <p className="micro-label">Settings</p>
          <h1 className="etched mt-1 truncate text-2xl font-semibold sm:text-3xl">Your account.</h1>
        </div>
        <span className="shrink-0 rounded-sm border border-border px-2 py-1 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
          {account.data?.plan.name ?? "…"}
        </span>
      </header>
      <p className="mt-2 truncate text-xs text-muted-foreground">
        <span className="notranslate">{account.data?.email ?? "…"}</span>
      </p>

      <div className="mt-6">
        {/* ── Account ───────────────────────────── */}
        <Panel
          id="account"
          title="Account"
          summary={`Theme · ${activeTheme.name}`}
          icon={<UserRound className="h-4 w-4" />}
          open={openSection === "account"}
          onToggle={toggleSection}
        >
          <p className="micro-label">Theme</p>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {THEMES.map((t) => {
              const on = t.id === theme;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTheme(t.id)}
                  aria-pressed={on}
                  title={t.blurb}
                  className={`flex min-h-11 items-center gap-2 rounded-sm border px-2 py-2 text-left transition-colors ${
                    on
                      ? "border-foreground/50 bg-secondary"
                      : "border-border hover:border-foreground/30"
                  }`}
                >
                  <span aria-hidden="true" className="flex shrink-0 gap-[2px]">
                    {t.swatch.map((c) => (
                      <span
                        key={c}
                        className="h-4 w-2 rounded-[2px] border border-black/20"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </span>
                  <span className="min-w-0 truncate text-xs text-foreground">{t.name}</span>
                </button>
              );
            })}
          </div>
        </Panel>

        {/* ── Access & security ─────────────────── */}
        <Panel
          id="security"
          title="Access & security"
          summary="Lock code and accessibility tools"
          icon={<Lock className="h-4 w-4" />}
          open={openSection === "security"}
          onToggle={toggleSection}
        >
          <p className="micro-label">Lock code</p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Change the 4-digit code you enter when the app opens.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Input
              inputMode="numeric"
              maxLength={4}
              placeholder="Current PIN"
              value={curPin}
              onChange={(e) => setCurPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="rounded-sm"
            />
            <Input
              inputMode="numeric"
              maxLength={4}
              placeholder="New PIN"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="rounded-sm"
            />
            <Input
              inputMode="numeric"
              maxLength={4}
              placeholder="Confirm new PIN"
              value={newPin2}
              onChange={(e) => setNewPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="rounded-sm"
            />
          </div>
          <Button
            variant="outline"
            className="mt-4 rounded-sm"
            disabled={pinBusy}
            onClick={async () => {
              if (!/^\d{4}$/.test(curPin) || !/^\d{4}$/.test(newPin)) {
                toast.error("Both codes need to be 4 digits");
                return;
              }
              if (newPin !== newPin2) {
                toast.error("The new codes don't match");
                return;
              }
              setPinBusy(true);
              try {
                const res = await runChangePin({ data: { currentPin: curPin, newPin } });
                if (res.ok) {
                  setCurPin("");
                  setNewPin("");
                  setNewPin2("");
                  toast.success("Lock code updated");
                } else if (res.reason === "locked") {
                  toast.error("Too many tries — try again in a few minutes");
                } else {
                  toast.error("That current code isn't right");
                }
              } catch (err) {
                toast.error(err instanceof Error ? err.message : "Could not update the code");
              } finally {
                setPinBusy(false);
              }
            }}
          >
            <KeyRound className="mr-2 h-4 w-4" />
            {pinBusy ? "Saving…" : "Update lock code"}
          </Button>

          <div className="mt-8 border-t border-border pt-6">
            <p className="micro-label">Auto-lock</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Lock the app back to the PIN screen after a stretch of no activity on this device.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {AUTO_LOCK_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setAutoLockMinutes(opt.value);
                    setAutoLock(opt.value);
                    toast.success(
                      opt.value === 0
                        ? "Auto-lock turned off"
                        : `Auto-lock set to ${opt.label.toLowerCase()}`,
                    );
                  }}
                  className={`rounded-sm border px-3 py-2 text-xs transition-colors ${
                    autoLock === opt.value
                      ? "border-primary bg-accent text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <p className="micro-label">Accessibility</p>
            <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-sm border border-border px-3 py-3">
              <div className="min-w-0">
                <p className="text-sm text-foreground">Accessibility tools</p>
                <p className="text-xs leading-snug text-muted-foreground">
                  Turn this off to remove the floating button and panel completely.
                </p>
              </div>
              <Switch
                className="shrink-0"
                checked={!a11yOff}
                onCheckedChange={async (next) => {
                  const off = !next;
                  setAccessibilityDisabled(off);
                  setA11yOff(off);
                  try {
                    await runSaveA11y({ data: { disabled: off } });
                  } catch {
                    setAccessibilityDisabled(!off);
                    setA11yOff(!off);
                    toast.error("Could not save preference to your account");
                    return;
                  }
                  toast.success(next ? "Accessibility tools on" : "Accessibility tools off");
                }}
              />
            </div>
            {!a11yOff && (
              <Button
                variant="outline"
                className="mt-4 rounded-sm"
                onClick={() => {
                  showAccessibilityMenu();
                  setA11yOff(false);
                  toast.success("Accessibility menu is visible again");
                }}
              >
                <Accessibility className="mr-2 h-4 w-4" />
                Show accessibility menu
              </Button>
            )}
          </div>
        </Panel>

        {/* ── Businesses ────────────────────────── */}
        <Panel
          id="businesses"
          title="Businesses"
          summary={`${businesses.length} ${businesses.length === 1 ? "business" : "businesses"} · reorder, rename, add, remove`}
          icon={<Briefcase className="h-4 w-4" />}
          open={openSection === "businesses"}
          onToggle={toggleSection}
        >
          <p className="text-sm leading-relaxed text-muted-foreground">
            Use the arrows to set the order they appear everywhere in the app. Removing a business
            also deletes its daily entries, journal, contacts, and interaction history.
          </p>

          <div className="mt-5 space-y-2">
            {orderedBusinesses.map((b, index) => (
              <div
                key={b.id}
                className="flex items-center gap-2 rounded-sm border border-border px-3 py-2"
              >
                {editingId === b.id ? (
                  <>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      disabled={businessBusy}
                      className="h-9 flex-1 rounded-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void saveEdit();
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                    <button
                      onClick={() => void saveEdit()}
                      disabled={businessBusy || !editName.trim()}
                      className="rounded-sm p-2 text-muted-foreground hover:text-foreground disabled:opacity-40"
                      aria-label="Save"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={businessBusy}
                      className="rounded-sm p-2 text-muted-foreground hover:text-foreground disabled:opacity-40"
                      aria-label="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex shrink-0 flex-col">
                      <button
                        onClick={() => void moveBusiness(b.id, -1)}
                        disabled={index === 0}
                        className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-25"
                        translate="no"
                        aria-label={`Move ${b.name} up`}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => void moveBusiness(b.id, 1)}
                        disabled={index === orderedBusinesses.length - 1}
                        className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-25"
                        translate="no"
                        aria-label={`Move ${b.name} down`}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="notranslate min-w-0 flex-1 truncate text-sm text-foreground">
                      {b.name}
                      {active?.id === b.id && (
                        <span className="ml-2 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
                          active
                        </span>
                      )}
                    </span>
                    <button
                      onClick={() => startEdit(b)}
                      className="shrink-0 rounded-sm p-2 text-muted-foreground hover:text-foreground"
                      translate="no"
                      aria-label={`Rename ${b.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeletingId(b.id)}
                      className="shrink-0 rounded-sm p-2 text-muted-foreground hover:text-destructive"
                      translate="no"
                      aria-label={`Delete ${b.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            ))}

            {deletingId && (
              <div className="rounded-sm border border-destructive/40 bg-destructive/10 p-4">
                <p className="text-sm text-foreground">
                  Delete this business and everything inside it? This cannot be undone.
                </p>
                <div className="mt-4 flex gap-3">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={businessBusy}
                    onClick={() => void confirmDelete(deletingId)}
                    className="rounded-sm"
                  >
                    Delete
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={businessBusy}
                    onClick={() => setDeletingId(null)}
                    className="rounded-sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New business name"
                disabled={businessBusy}
                className="h-10 min-w-0 flex-1 rounded-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter") void addBusiness();
                }}
              />
              <Button
                variant="outline"
                disabled={businessBusy || !newName.trim()}
                onClick={() => void addBusiness()}
                className="h-10 shrink-0 rounded-sm"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </Panel>

        {/* ── Your app ──────────────────────────── */}
        <Panel
          id="app"
          title="Your app"
          summary={
            hiddenCount > 0
              ? `Menu sections, help, feedback · ${hiddenCount} hidden`
              : "Menu sections, help, feedback"
          }
          icon={<SlidersHorizontal className="h-4 w-4" />}
          open={openSection === "app"}
          onToggle={toggleSection}
        >
          <p className="micro-label">Menu sections</p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Turn off anything you don't use. Hidden sections keep their data. Today and Settings
            always stay.
          </p>
          <div className="mt-4 divide-y divide-border rounded-sm border border-border">
            {SECTION_HELP.filter((s) => s.hideable).map((s) => {
              const on = !hidden.includes(s.key);
              return (
                <div
                  key={s.key}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-foreground">{s.label}</p>
                    <p className="text-xs leading-snug text-muted-foreground">{s.what}</p>
                  </div>
                  <Switch
                    className="shrink-0"
                    checked={on}
                    onCheckedChange={(next) =>
                      setHidden(next ? hidden.filter((k) => k !== s.key) : [...hidden, s.key])
                    }
                    aria-label={`Show ${s.label}`}
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <p className="micro-label">Help</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              The walkthrough of every section is always available — here or from the ? in the
              header.
            </p>
            <Button variant="outline" className="mt-4 rounded-sm" onClick={openHelp}>
              <HelpCircle className="mr-2 h-4 w-4" />
              Open the walkthrough
            </Button>
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <p className="micro-label flex items-center gap-2">
              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
              Business glossary
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Plain-language definitions of the terms that come up while running a business — cap
              table, burn rate, runway, CAC, margin, and the rest.
            </p>
            <Button asChild variant="outline" className="mt-4 rounded-sm">
              <Link to="/glossary" target="_blank">
                Open the glossary
              </Link>
            </Button>
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <p className="micro-label flex items-center gap-2">
              <Smartphone className="h-3.5 w-3.5" aria-hidden="true" />
              Add to your home screen
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Add The Founders App to your home screen for a full-screen, app-like experience — no
              App Store needed.
            </p>
            <ul className="mt-3 list-disc space-y-1.5 pl-4 text-sm leading-relaxed text-muted-foreground">
              <li>
                <strong className="text-foreground/80">iPhone / iPad (Safari):</strong> Tap the
                Share button, then "Add to Home Screen".
              </li>
              <li>
                <strong className="text-foreground/80">Android (Chrome):</strong> Tap the menu (⋮),
                then "Install app" or "Add to Home Screen".
              </li>
              <li>
                <strong className="text-foreground/80">Desktop (Chrome / Edge):</strong> Click the
                install icon in the address bar.
              </li>
            </ul>
          </div>

          <BetaProgress />

          <div className="mt-8 border-t border-border pt-6">
            <p className="micro-label">Report a bug or send an idea</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Anything broken, missing, or worth trying — write it here. It lands straight with us.
            </p>

            <div className="mt-4 flex gap-2">
              {(["bug", "idea"] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFeedbackCategory(c)}
                  aria-pressed={feedbackCategory === c}
                  className={`min-h-9 rounded-sm border px-4 text-xs uppercase tracking-wider transition-colors ${
                    feedbackCategory === c
                      ? "border-foreground/50 bg-secondary text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c === "bug" ? "Bug" : "Idea"}
                </button>
              ))}
            </div>
            <Textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={
                feedbackCategory === "bug"
                  ? "What happened, and what did you expect instead?"
                  : "What would make this better?"
              }
              rows={4}
              className="mt-4 rounded-sm"
            />
            <Button
              className="mt-4 rounded-sm"
              disabled={feedbackBusy}
              onClick={() => {
                if (!feedbackText.trim()) {
                  toast.error("Write something first");
                  return;
                }
                void sendFeedback();
              }}
            >
              <MessageSquarePlus className="mr-2 h-4 w-4" />
              {feedbackBusy ? "Sending…" : "Send"}
            </Button>
            {myFeedback.data && myFeedback.data.length > 0 && (
              <ul className="mt-6 space-y-2 border-t border-border pt-5">
                {myFeedback.data.map((f) => (
                  <li key={f.id} className="text-xs text-muted-foreground">
                    <span className="uppercase tracking-wider text-foreground/70">
                      {f.category}
                    </span>{" "}
                    · {new Date(f.created_at).toLocaleDateString()} —{" "}
                    <span className="notranslate">{f.message.slice(0, 90)}</span>
                    {f.message.length > 90 ? "…" : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>

        {/* ── Invite & earn ─────────────────────── */}
        <Panel
          id="invite"
          title="Invite & earn"
          summary="Share your link, earn free months"
          icon={<Gift className="h-4 w-4" />}
          open={openSection === "invite"}
          onToggle={toggleSection}
        >
          <InviteCard />
        </Panel>

        {/* ── Data ──────────────────────────────── */}
        <Panel
          id="data"
          title="Data"
          summary="Export everything, or restore from trash"
          icon={<Database className="h-4 w-4" />}
          open={openSection === "data"}
          onToggle={toggleSection}
        >
          <p className="micro-label">Export</p>
          <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Export format">
            {(
              [
                { id: "json", label: "JSON" },
                { id: "csv", label: "CSV" },
                { id: "markdown", label: "Markdown for AI" },
              ] as const
            ).map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setExportFormat(f.id)}
                aria-pressed={exportFormat === f.id}
                className={`min-h-9 rounded-sm border px-4 text-xs uppercase tracking-wider transition-colors ${
                  exportFormat === f.id
                    ? "border-foreground/50 bg-secondary text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.id === "markdown" && <Sparkles className="mr-1.5 inline h-3 w-3" />}
                {f.label}
              </button>
            ))}
          </div>

          {exportFormat === "markdown" ? (
            <>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                A readable log of one business over a date range — daily moves and outcomes, bold
                moves, journal pages, ideas, appointments, and follow-ups, in order. Paste it into
                an AI chat and ask what patterns it sees.
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <div className="min-w-0">
                  <label
                    htmlFor="ai-business"
                    className="block text-xs uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Business
                  </label>
                  <select
                    id="ai-business"
                    value={aiBusinessId}
                    onChange={(e) => setAiBusinessId(e.target.value)}
                    className="mt-2 h-10 w-full rounded-sm border border-border bg-background px-3 text-sm"
                  >
                    {businesses.map((b) => (
                      <option key={b.id} value={b.id} className="notranslate">
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label
                    htmlFor="ai-start"
                    className="block text-xs uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Start date
                  </label>
                  <Input
                    id="ai-start"
                    type="date"
                    value={aiStart}
                    onChange={(e) => setAiStart(e.target.value)}
                    className="mt-2 h-10 rounded-sm"
                  />
                </div>
                <div className="min-w-0">
                  <label
                    htmlFor="ai-end"
                    className="block text-xs uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    End date
                  </label>
                  <Input
                    id="ai-end"
                    type="date"
                    value={aiEnd}
                    onChange={(e) => setAiEnd(e.target.value)}
                    className="mt-2 h-10 rounded-sm"
                  />
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="rounded-sm"
                  disabled={aiBusy || !aiBusinessId}
                  onClick={() => void runAiExport("download")}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Markdown
                </Button>
                <Button
                  variant="outline"
                  className="rounded-sm"
                  disabled={aiBusy || !aiBusinessId}
                  onClick={() => void runAiExport("copy")}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy to clipboard
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                A single file with every business, daily entry, bold move, journal page, idea,
                appointment, contact, and interaction. Yours to keep, whatever you decide next.
              </p>
              <Button
                variant="outline"
                className="mt-5 rounded-sm"
                onClick={() => void downloadExport(exportFormat)}
              >
                <Download className="mr-2 h-4 w-4" />
                Download {exportFormat.toUpperCase()}
              </Button>
            </>
          )}

          <div className="mt-8 border-t border-border pt-6">
            <TrashCard bare />
          </div>
        </Panel>

        {/* ── Danger zone ───────────────────────── */}
        <Panel
          id="danger"
          title="Danger zone"
          summary="Permanently delete your account"
          icon={<ShieldAlert className="h-4 w-4" />}
          open={openSection === "danger"}
          onToggle={toggleSection}
          danger
        >
          <button
            type="button"
            onClick={() => setDangerOpen((v) => !v)}
            aria-expanded={dangerOpen}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              aria-hidden="true"
              className={`h-4 w-4 transition-transform ${dangerOpen ? "rotate-180" : ""}`}
            />
            What happens when I delete my account?
          </button>
          {dangerOpen && (
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              This removes your sign-in and permanently erases every business, daily entry, bold
              move, journal page, contact, and interaction log. It happens immediately and cannot be
              undone — there is no recovery window and we keep no copy. Export first if you want a
              record.
            </p>
          )}

          <label
            htmlFor="delete-confirm"
            className="mt-6 block text-xs uppercase tracking-[0.18em] text-muted-foreground"
          >
            Type DELETE to confirm
          </label>
          <Input
            id="delete-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            spellCheck={false}
            className="mt-3 max-w-xs rounded-sm tracking-[0.3em]"
          />

          <Button
            variant="destructive"
            disabled={!armed || busy}
            onClick={() => void wipe()}
            className="mt-5 rounded-sm"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {busy ? "Deleting…" : "Delete my account permanently"}
          </Button>
        </Panel>
      </div>
    </div>
  );
}
